import {
  Avatar,
  AvatarTypeMap,
  Badge,
  BadgeTypeMap,
  Button,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  ListItemTypeMap,
  Switch,
  SwitchProps,
  SxProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { ReactNode, SyntheticEvent } from 'react';

import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { mergeSxProps } from '~/utils/helpers/sxProps';

import { useSxStyles } from './useStyles';

interface CustomSxStyles {
  listItemRoot?: SxProps<Theme>;
  listItemTextPrimary?: SxProps<Theme>;
  avatar?: SxProps<Theme>;
}

export interface Props {
  avatarProps?: AvatarTypeMap['props'];
  badgeProps?: BadgeTypeMap['props'];
  switchProps?: SwitchProps;
  listItemProps?: ListItemTypeMap<{}, 'div'>['props'];
  href?: string;
  secondaryTypographyProps?: Partial<TypographyProps<'div'>>;
  primaryElement: string | JSX.Element;
  secondaryElement?: string | JSX.Element;
  secondaryActions?: ReactNode;
  isActive?: boolean;
  onClick?: (event: SyntheticEvent) => void;
  isOldUi?: boolean;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
  customSxStyles?: CustomSxStyles;
}

export const SummaryItem: React.FC<Props> = (props) => {
  const {
    avatarProps,
    badgeProps,
    href,
    switchProps,
    listItemProps,
    secondaryTypographyProps,
    primaryElement,
    secondaryElement,
    onClick,
    secondaryActions,
    isActive,
    isOldUi,
    style,
    sx,
    customSxStyles,
  } = props;
  const sxStyles = useSxStyles();

  const customSx = {
    listItemRoot: mergeSxProps(
      sxStyles.listItemRoot,
      customSxStyles?.listItemRoot,
    ),
    avatar: mergeSxProps(
      isOldUi ? sxStyles.avatarOld : sxStyles.avatar,
      sx,
      customSxStyles?.avatar,
    ),
    listItemTextPrimary: mergeSxProps(
      sxStyles.listItemTextPrimary,
      customSxStyles?.listItemTextPrimary,
    ),
    listItemTextSecondary: mergeSxProps(
      sxStyles.listItemTextSecondary,
      isOldUi ? sxStyles.listItemTextSecondaryOldUi : undefined,
    ),
  };

  return (
    <ListItem
      sx={customSx.listItemRoot}
      /* TypeScript discriminators limitation. See https://github.com/mui-org/material-ui/issues/14971.
         Mui uses discriminators, so button can either be false | undefined, either true, but not boolean or boolean | undefined.
      */
      // @ts-ignore
      button={!!onClick as true}
      onMouseDown={onClick}
      href={href}
      component={href || !!onClick ? Button : 'li'}
      {...listItemProps}
      selected={isActive}
      data-cy="list-item"
      data-selected={isActive}
      style={style}
    >
      {!!switchProps && (
        <Switch {...switchProps} color="primary" data-cy="toggle" />
      )}
      {!!avatarProps && (
        <ListItemAvatar sx={isOldUi ? sxStyles.listItemAvatarOld : undefined}>
          {!!badgeProps ? (
            <Badge
              {...badgeProps}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              sx={sxStyles.badge}
            >
              <Avatar
                {...avatarProps}
                className={'avatar'}
                data-cy="profile-pic"
                sx={customSx.avatar}
              />
            </Badge>
          ) : (
            <Avatar
              {...avatarProps}
              className={'avatar'}
              data-cy="profile-pic"
              sx={customSx.avatar}
            />
          )}
        </ListItemAvatar>
      )}
      <ListItemText
        sx={sxStyles.listItemTextMultiline}
        primary={
          <Typography
            component="div"
            sx={customSx.listItemTextPrimary}
            data-cy="primary"
          >
            <TooltippedTruncatedText>{primaryElement}</TooltippedTruncatedText>
          </Typography>
        }
        secondary={
          secondaryElement && (
            <Typography
              component="div"
              color="textSecondary"
              sx={customSx.listItemTextSecondary}
              data-cy="secondary"
              {...secondaryTypographyProps}
            >
              <TooltippedTruncatedText>
                {isOldUi && (
                  <LegacyFAIcon
                    FAIcon={LegacyFAIcons.Clock}
                    sx={sxStyles.clockIcon}
                  />
                )}
                {secondaryElement}
                {isOldUi && (
                  <LegacyFAIcon
                    FAIcon={LegacyFAIcons.Clock}
                    sx={sxStyles.userIcon}
                  />
                )}
              </TooltippedTruncatedText>
            </Typography>
          )
        }
      />
      {secondaryActions && (
        <ListItemSecondaryAction>{secondaryActions}</ListItemSecondaryAction>
      )}
    </ListItem>
  );
};
